import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { getAdminAlertSnapshot, getAdminAlertSnapshots } from 'utils/api/admin';

const STATUS_LABEL = {
  pending: '발송중',
  sent: '발송완료',
  failed: '실패',
};

const formatDate = (d) => (d ? new Date(d).toLocaleString('ko-KR') : '-');
const formatPrice = (v) => {
  const n = Number(v);
  if (!n) return '-';
  return `${(n / 10000).toFixed(2)}억`;
};

const AlertSnapshots = () => {
  const [snapshots, setSnapshots] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [userEmailInput, setUserEmailInput] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const size = 20;

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status, userEmail, dateFrom, dateTo]);

  const fetchList = async () => {
    try {
      const res = await getAdminAlertSnapshots({
        status,
        userEmail,
        dateFrom,
        dateTo,
        page,
        size,
      });
      setSnapshots(res.results.snapshots);
      setTotal(res.results.total);
    } catch (err) {
      console.error(err);
    }
  };

  const onSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setUserEmail(userEmailInput.trim());
  };

  const openDetail = async (id) => {
    setDetailLoading(true);
    setDetail({ _id: id, items: [], loading: true });
    try {
      const res = await getAdminAlertSnapshot(id);
      setDetail(res.results);
    } catch (err) {
      console.error(err);
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => setDetail(null);

  const totalPages = Math.ceil(total / size);

  return (
    <Container>
      <Title>급매 알림 내역</Title>
      <Hint>
        스케줄러가 매물을 급매로 판정해서 메일을 보낸 시점의 스냅샷입니다. 행을
        클릭하면 매칭된 매물 목록이 보입니다.
      </Hint>
      <FilterRow as="form" onSubmit={onSearch}>
        <Select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="">전체 상태</option>
          <option value="sent">발송완료</option>
          <option value="failed">실패</option>
          <option value="pending">발송중</option>
        </Select>
        <Input
          type="text"
          placeholder="유저 이메일 검색"
          value={userEmailInput}
          onChange={(e) => setUserEmailInput(e.target.value)}
        />
        <Input
          type="date"
          value={dateFrom}
          onChange={(e) => {
            setDateFrom(e.target.value);
            setPage(1);
          }}
        />
        <span>~</span>
        <Input
          type="date"
          value={dateTo}
          onChange={(e) => {
            setDateTo(e.target.value);
            setPage(1);
          }}
        />
        <SearchBtn type="submit">검색</SearchBtn>
        <Info>총 {total}건</Info>
      </FilterRow>
      <Table>
        <thead>
          <tr>
            <Th>발송일</Th>
            <Th>유저</Th>
            <Th>구독지역</Th>
            <Th>매칭 합계</Th>
            <Th>KB↓</Th>
            <Th>평균↓</Th>
            <Th>상태</Th>
            <Th>발송시각</Th>
          </tr>
        </thead>
        <tbody>
          {snapshots.map((s) => (
            <Row key={s._id} onClick={() => openDetail(s._id)}>
              <Td>{s.date}</Td>
              <Td>
                <div>{s.userName || '-'}</div>
                <div style={{ fontSize: '12px', color: '#8f9bba' }}>
                  {s.userEmail}
                </div>
              </Td>
              <Td style={{ maxWidth: '260px' }}>
                {(s.regions || []).join(', ')}
              </Td>
              <Td>{s.totalCount}건</Td>
              <Td>{s.belowKbCount}</Td>
              <Td>{s.belowAvgCount}</Td>
              <Td>
                <StatusBadge status={s.emailStatus}>
                  {STATUS_LABEL[s.emailStatus] || s.emailStatus}
                </StatusBadge>
                {s.emailErrorMessage && (
                  <ErrorMsg title={s.emailErrorMessage}>
                    {s.emailErrorMessage}
                  </ErrorMsg>
                )}
              </Td>
              <Td>{formatDate(s.sentAt || s.createdAt)}</Td>
            </Row>
          ))}
          {snapshots.length === 0 && (
            <tr>
              <Td colSpan={8} style={{ textAlign: 'center', color: '#8f9bba' }}>
                알림 내역이 없습니다.
              </Td>
            </tr>
          )}
        </tbody>
      </Table>
      {totalPages > 1 && (
        <Pagination>
          <PageBtn disabled={page <= 1} onClick={() => setPage(page - 1)}>
            이전
          </PageBtn>
          <PageInfo>
            {page} / {totalPages}
          </PageInfo>
          <PageBtn
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            다음
          </PageBtn>
        </Pagination>
      )}

      {detail && (
        <ModalBackdrop onClick={closeDetail}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <div>
                <ModalTitle>매칭 매물 상세</ModalTitle>
                {!detailLoading && (
                  <ModalSub>
                    {detail.userEmail} · {detail.date} · 총{' '}
                    {detail.totalCount || 0}건 (KB하한↓{' '}
                    {detail.belowKbLowCount || 0} / KB상한↓{' '}
                    {detail.belowKbUpperCount || 0} / 평균↓{' '}
                    {detail.belowAvgCount || 0})
                  </ModalSub>
                )}
              </div>
              <CloseBtn onClick={closeDetail}>닫기</CloseBtn>
            </ModalHeader>
            {detailLoading ? (
              <Loading>불러오는 중…</Loading>
            ) : (
              <ItemTable>
                <thead>
                  <tr>
                    <Th>단지</Th>
                    <Th>평형</Th>
                    <Th>층</Th>
                    <Th>호가</Th>
                    <Th>KB 상한가</Th>
                    <Th>KB 하한가</Th>
                    <Th>단지평균</Th>
                    <Th>판정</Th>
                  </tr>
                </thead>
                <tbody>
                  {(detail.items || []).map((it, idx) => (
                    <tr key={it.atcl_no || idx}>
                      <Td>
                        <div>{it.hscp_nm || it.hscp_no || '-'}</div>
                        <div style={{ fontSize: '11px', color: '#8f9bba' }}>
                          {it.atcl_no}
                        </div>
                      </Td>
                      <Td>{it.spc_type_cd || '-'}</Td>
                      <Td>{it.flr_info || '-'}</Td>
                      <Td>{formatPrice(it.prc_info)}</Td>
                      <Td>{formatPrice(it.kb_deal_upper_price)}</Td>
                      <Td>{formatPrice(it.kb_deal_low_price)}</Td>
                      <Td>{formatPrice(it.type1_avg_prc_info)}</Td>
                      <Td>
                        {it.isBelowKbLow && <Tag color="#2c5282">KB하한↓</Tag>}
                        {it.isBelowKbUpper && (
                          <Tag color="#3182ce">KB상한↓</Tag>
                        )}
                        {it.isBelowAvg && <Tag color="#e53e3e">평균↓</Tag>}
                      </Td>
                    </tr>
                  ))}
                  {(detail.items || []).length === 0 && (
                    <tr>
                      <Td
                        colSpan={8}
                        style={{ textAlign: 'center', color: '#8f9bba' }}
                      >
                        매물이 없습니다.
                      </Td>
                    </tr>
                  )}
                </tbody>
              </ItemTable>
            )}
          </Modal>
        </ModalBackdrop>
      )}
    </Container>
  );
};

const Container = styled.div``;
const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #232d42;
  margin-bottom: 8px;
`;
const Hint = styled.div`
  font-size: 13px;
  color: #8f9bba;
  margin-bottom: 16px;
`;
const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;
const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
`;
const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
`;
const SearchBtn = styled.button`
  padding: 8px 16px;
  border: 1px solid #232d42;
  border-radius: 8px;
  background: #232d42;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
`;
const Info = styled.div`
  font-size: 13px;
  color: #8f9bba;
  margin-left: auto;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
`;
const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #8f9bba;
  border-bottom: 1px solid #e4e4e4;
  background: #fafafa;
`;
const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #232d42;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: top;
`;
const Row = styled.tr`
  cursor: pointer;
  &:hover {
    background: #fafafa;
  }
`;
const StatusBadge = styled.span`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ status }) =>
    status === 'sent'
      ? '#e6ffed'
      : status === 'failed'
      ? '#fff5f5'
      : '#fff8e1'};
  color: ${({ status }) =>
    status === 'sent'
      ? '#38a169'
      : status === 'failed'
      ? '#e53e3e'
      : '#b7791f'};
`;
const ErrorMsg = styled.div`
  font-size: 11px;
  color: #e53e3e;
  margin-top: 4px;
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 20px;
`;
const PageBtn = styled.button`
  padding: 6px 16px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  background: #fff;
  font-size: 13px;
  cursor: pointer;
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;
const PageInfo = styled.span`
  font-size: 13px;
  color: #8f9bba;
`;
const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 20px;
`;
const Modal = styled.div`
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 1000px;
  max-height: 85vh;
  overflow: auto;
  padding: 24px;
`;
const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
`;
const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #232d42;
  margin: 0 0 6px 0;
`;
const ModalSub = styled.div`
  font-size: 13px;
  color: #8f9bba;
`;
const CloseBtn = styled.button`
  padding: 6px 14px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  background: #fff;
  font-size: 13px;
  cursor: pointer;
`;
const ItemTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`;
const Loading = styled.div`
  text-align: center;
  padding: 40px;
  color: #8f9bba;
`;
const Tag = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  color: ${({ color }) => color};
  border: 1px solid ${({ color }) => color};
  margin-right: 4px;
`;

export default AlertSnapshots;
